import { useState } from 'react';
import { formatMoney } from '@/utils/format';

interface TopUpBankDetailsProps {
  bankName: string;
  accountName: string;
  accountNumber: string;
  swiftCode: string;
  currency: string;
  amount: number;
}

export default function TopUpBankDetails({
  bankName,
  accountName,
  accountNumber,
  swiftCode,
  currency,
  amount,
}: TopUpBankDetailsProps) {
  const [copied, setCopied] = useState('');

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied((current) => (current === label ? '' : current)), 1800);
    } catch {
      setCopied('');
    }
  };

  const rows = [
    { label: 'Bank Name', value: bankName, copyable: false },
    { label: 'Account Name', value: accountName, copyable: true },
    { label: 'Account No.', value: accountNumber, copyable: true },
    { label: 'SWIFT Code', value: swiftCode, copyable: true },
  ];

  return (
    <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-4">
      <p className="flex items-center gap-2 text-sm font-semibold text-secondary-900">
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-bank-line" />
        </span>
        Please make the payments to:
      </p>

      <dl className="mt-3 overflow-hidden rounded-md border border-secondary-200 bg-background-50">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between gap-3 border-b border-secondary-100 px-3 py-2.5 last:border-0"
          >
            <dt className="shrink-0 text-xs text-foreground-500">{row.label}</dt>
            <dd className="flex min-w-0 items-center gap-2">
              <span className="truncate text-sm font-medium text-foreground-900 tabular-nums">
                {row.value}
              </span>
              {row.copyable ? (
                <button
                  type="button"
                  onClick={() => copy(row.label, row.value)}
                  aria-label={`Copy ${row.label}`}
                  className="inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-sm text-foreground-500 transition-colors hover:bg-background-100 hover:text-secondary-900"
                >
                  <i className={copied === row.label ? 'ri-check-line' : 'ri-file-copy-line'} />
                </button>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>

      <div className="mt-3 flex items-center justify-between rounded-md border border-secondary-200 bg-background-50 px-3 py-2.5">
        <span className="text-xs text-foreground-500">Amount to send</span>
        <span className="font-heading text-sm font-semibold text-foreground-950">
          {formatMoney(amount, currency)}
        </span>
      </div>
    </div>
  );
}